import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import { getPostPlainBody, postNo } from "@/entities/post/types";
import { normalizeSearchPostAttachments } from "@/entities/thread/normalizeAttachments";
import type { SearchResponse } from "@/entities/thread/types";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import { DesktopSearchToolbar } from "@/features/search/components/DesktopSearchToolbar";
import { SearchScopeSelect } from "@/features/search/components/SearchScopeSelect";
import { getPublicSearchPath } from "@/features/search/publicSearch";
import { resolveSearchResultThreadRoute } from "@/features/search/searchResultLink";
import {
  normalizeSearchScope,
  serializeSearchScope,
} from "@/features/search/searchScope";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import { apiGet } from "@/shared/api/client";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useIsDesktop } from "@/shared/hooks/useMediaQuery";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { Message } from "@/shared/ui/feedback";
import { getApiErrorMessage } from "@/shared/ui/feedback/apiErrorMessage";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";
import { Button, Input } from "@/shared/ui/form";

interface SearchQueryStateProps {
  children: React.ReactNode;
  error: unknown;
  hasData: boolean;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}

export const SearchQueryState: React.FunctionComponent<
  SearchQueryStateProps
> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying,
  onRetry,
}: SearchQueryStateProps) => (
  <QueryStateBoundary
    error={error}
    hasData={hasData}
    isLoading={isLoading}
    isRetrying={isRetrying}
    message={getApiErrorMessage(error, "検索に失敗しました")}
    onRetry={onRetry}
  >
    {children}
  </QueryStateBoundary>
);

export const SearchPage: React.FunctionComponent = () => {
  useDocumentTitle(`投稿検索 - ${BOARD_DISPLAY_NAME}`);
  const isDesktop = useIsDesktop();
  const { q = "", scope: serializedScope } = useSearch({ from: "/search" });
  const navigate = useNavigate({ from: "/search" });
  const scope = normalizeSearchScope(serializedScope);
  const { setSearchQuery, setSearchScope } = useCatalogStore();
  const [draft, setDraft] = useState({ source: q, value: q });
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (!isDesktop) setIsSettingsOpen(false);
  }
  const input = draft.source === q ? draft.value : q;
  const normalizedQuery = q.trim();
  useEffect(() => {
    setSearchQuery(q);
    setSearchScope(scope);
  }, [q, scope, setSearchQuery, setSearchScope]);
  const searchQuery = useQuery({
    queryKey: ["public-post-search", normalizedQuery, scope],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      apiGet<SearchResponse>(getPublicSearchPath(normalizedQuery, scope), {
        signal,
        cache: "no-store",
      }).then((response) => ({
        ...response,
        posts: normalizeSearchPostAttachments(response.posts),
      })),
    enabled: normalizedQuery.length > 0,
    staleTime: 0,
  });

  const submit = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void navigate({
      search: {
        q: input.trim(),
        scope: serializeSearchScope(scope),
      },
    });
  };

  const changeScope = (nextScope: typeof scope): void => {
    void navigate({
      search: {
        q: q || undefined,
        scope: serializeSearchScope(nextScope),
      },
    });
  };

  return (
    <section
      aria-labelledby="public-search-page-title"
      className={isDesktop ? "desktop-catalog-page" : "public-search-page"}
    >
      {isDesktop && (
        <DesktopSearchToolbar
          onToggleSidebar={(): void =>
            desktopHistorySidebarRef.current?.toggle()
          }
          onScrollToTop={(): void => window.scrollTo({ top: 0 })}
          onScrollToBottom={(): void =>
            window.scrollTo({ top: document.documentElement.scrollHeight })
          }
          onSettingsClick={(
            event: React.MouseEvent<HTMLAnchorElement>,
          ): void => {
            if (hasLinkModifierKey(event)) return;
            event.preventDefault();
            setIsSettingsOpen(true);
          }}
        />
      )}
      {isDesktop && (
        <h1 id="public-search-page-title" className="desktop-mode-title">
          投稿検索
        </h1>
      )}
      <div className={isDesktop ? "public-search-page" : undefined}>
        {!isDesktop && (
          <>
            <nav aria-label="投稿検索メニュー">
              <Link to="/">カタログ</Link>
              <Link to="/board">リスト</Link>
            </nav>
            <h1 id="public-search-page-title">掲示板全体の投稿検索</h1>
          </>
        )}
        <p>公開中のライブ投稿を新しい順に最大100件表示します。</p>
        <search aria-label="掲示板全体の投稿検索">
          <form onSubmit={submit} className="public-search-form">
            <SearchScopeSelect
              ariaLabel="掲示板全体の検索モード"
              scope={scope}
              onChange={changeScope}
            />
            <Input
              value={input}
              maxLength={100}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                setDraft({ source: q, value: event.target.value })
              }
              aria-label="掲示板全体の投稿検索"
              className="min-w-0 flex-1"
            />
            <Button
              type="submit"
              disabled={!input.trim() || searchQuery.isFetching}
            >
              {searchQuery.isFetching ? "検索中" : "検索"}
            </Button>
          </form>
        </search>
        {/*
          検索語が空のときクエリは enabled:false で走らない。この状態を
          QueryStateBoundary へ渡すと data も error も loading もない状態として
          「データを読み込めませんでした」に落ちるため、境界の外で分岐する。
        */}
        {!normalizedQuery ? (
          <Message variant="info">検索語を入力してください</Message>
        ) : (
          <SearchQueryState
            error={searchQuery.error}
            hasData={searchQuery.data !== undefined}
            isLoading={searchQuery.isLoading}
            isRetrying={searchQuery.isFetching}
            onRetry={(): void => void searchQuery.refetch()}
          >
            {searchQuery.data?.posts.length === 0 ? (
              <Message variant="info">一致する公開投稿はありません</Message>
            ) : (
              <ol className="public-search-results" aria-live="polite">
                {searchQuery.data?.posts.map((post) => (
                  <li key={post.id}>
                    <SearchResultThreadLink
                      threadId={post.threadId}
                      postId={post.id}
                    >
                      No.{postNo(post)} / スレッドへ
                    </SearchResultThreadLink>
                    <p>{getPostPlainBody(post)}</p>
                  </li>
                ))}
              </ol>
            )}
          </SearchQueryState>
        )}
      </div>
      {isDesktop && (
        <>
          <DesktopHistorySidebar
            ref={desktopHistorySidebarRef}
            showClosedHandle={false}
          />
          <DesktopSettingsModal
            isOpen={isSettingsOpen}
            onClose={(): void => setIsSettingsOpen(false)}
          />
        </>
      )}
    </section>
  );
};

function SearchResultThreadLink({
  children,
  postId,
  threadId,
}: {
  children: React.ReactNode;
  postId: string;
  threadId: string;
}): React.ReactNode {
  const route = resolveSearchResultThreadRoute(threadId, postId);
  if (route.to === "/$slug/thread/$threadId") {
    return (
      <Link
        to={route.to}
        params={route.params}
        hash={route.hash}
        hashScrollIntoView={{ block: "center" }}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      to={route.to}
      params={route.params}
      hash={route.hash}
      hashScrollIntoView={{ block: "center" }}
    >
      {children}
    </Link>
  );
}
